// InvoicePenjualan.jsx
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Download,
  Search,
  Eye,
  Edit,
  Copy,
  MoreVertical,
  Ban,
  Send,
  CheckCircle2,
  Trash2,
  Upload,
  ChevronDown,
  X,
  Wallet,
} from "lucide-react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import InvoiceForm from "../others/InvoiceForm";
import InvoiceViewModal from "../others/InvoiceViewModal";

const API_BASE = ""; // same origin (proxy will forward to backend)

function toOrdinal(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return "";

  const mod100 = x % 100;
  let suffix = "th";

  if (!(mod100 >= 11 && mod100 <= 13)) {
    const mod10 = x % 10;
    if (mod10 === 1) suffix = "st";
    else if (mod10 === 2) suffix = "nd";
    else if (mod10 === 3) suffix = "rd";
  }

  return `${x}${suffix} Term`;
}
  

/* ===========================
 * Auth helpers
 * =========================== */
async function getFirebaseIdToken() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken(false);
}

async function apiFetch(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
    credentials: "include",
    ...options,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" && data) ||
      `Request failed (${res.status})`;
    throw new Error(message);
  }

  return data;
}

async function apiFetchBlob(path, options = {}) {
  const token = await getFirebaseIdToken();
  if (!token) throw new Error("No token provided (user not logged in)");

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };

  if (options.body && !("Content-Type" in headers)) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed (${res.status})`);
  }
  return await res.blob();
}

/** Upload file to a GCS signed URL (PUT) */
async function uploadToSignedUrl({ signedUrl, file, contentType }) {
  const res = await fetch(signedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType || "application/pdf" },
    body: file,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Upload failed (${res.status})`);
  }
  return true;
}

function safeDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().slice(0, 10);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "invoice.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Visible statuses for this page (exclude void/cancelled) */
const VISIBLE_STATUSES = ["draft", "issued", "overdue", "sent", "paid"];

/** Default checked statuses (paid NOT checked by default) */
const DEFAULT_STATUS_FILTER = ["draft", "issued", "overdue", "sent"];

const statusConfig = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-700" },
  issued: { label: "Issued", className: "bg-indigo-100 text-indigo-700" },
  sent: { label: "Sent", className: "bg-sky-100 text-sky-700" },
  paid: { label: "Paid", className: "bg-green-100 text-green-700" },
  overdue: { label: "Overdue", className: "bg-red-100 text-red-700" },
  // paid_overdue: { label: "Paid Overdue", className: "bg-orange-100 text-orange-700" },
};

/** Toast helpers */
function showErr(err, fallback = "Terjadi kesalahan") {
  const msg = String(err?.message || err || fallback);
  toast.error(msg);
}
function showOk(msg) {
  toast.success(msg);
}
function showInfo(msg) {
  toast.info(msg);
}

/* =========================================================
 * Small hooks: debounce + simple cache (SWR-ish)
 * ========================================================= */
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), Math.max(0, delayMs || 0));
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const INVOICE_CACHE_KEY = "invoice_tracker_invoice_penjualan_cache_v2";
const CACHE_TTL_MS = 60_000;

function loadInvoiceCache() {
  try {
    const raw = sessionStorage.getItem(INVOICE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveInvoiceCache(payload) {
  try {
    sessionStorage.setItem(INVOICE_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

/* =========================================================
 * Portal dropdown (prevents clipping by table overflow)
 * FIX: avoid flicker at (0,0) by using useLayoutEffect + pos=null guard
 * ========================================================= */
function ActionMenuPortal({ open, anchorRect, onClose, children }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null); // null until computed

  // Compute position BEFORE paint to avoid top-left flash
  useLayoutEffect(() => {
    if (!open || !anchorRect) {
      setPos(null);
      return;
    }

    const desiredWidth = 240;
    const gap = 8;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // initial: align right edge to anchor's right edge
    let left = anchorRect.right - desiredWidth;
    let top = anchorRect.bottom + gap;

    // clamp horizontally
    left = Math.max(8, Math.min(left, vw - desiredWidth - 8));
    // basic vertical clamp (top may be flipped later)
    top = Math.max(8, top);

    setPos({ top, left });

    // Flip above if bottom overflows (needs menu height, so measure after mount)
    requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom > vh - 8) {
        const flippedTop = Math.max(8, anchorRect.top - r.height - gap);
        setPos((p) => (p ? { ...p, top: flippedTop } : { top: flippedTop, left }));
      }
    });
  }, [open, anchorRect]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const el = menuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose?.();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onScroll = () => onClose?.();
    const onResize = () => onClose?.();

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, onClose]);

  // ✅ key part: don't render until positioned
  if (!open || !anchorRect || !pos) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <div
        ref={menuRef}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="pointer-events-auto w-[240px] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
        style={{ position: "fixed", top: `${pos.top}px`, left: `${pos.left}px` }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}


/* =========================================================
 * Multi-select status filter (portal)
 * ========================================================= */
function StatusMultiSelect({ value, onChange, options, disabled }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const close = useCallback(() => {
    setOpen(false);
    setAnchorRect(null);
  }, []);

  const normalized = Array.isArray(value) ? value : [];
  const label =
    normalized.length === 0
      ? "Status: (none)"
      : normalized.length === options.length
      ? "Semua Status"
      : `Status: ${normalized.length}`;

  const toggleOne = (st) => {
    const s = String(st || "").toLowerCase();
    const next = normalized.includes(s) ? normalized.filter((x) => x !== s) : [...normalized, s];
    onChange?.(next);
  };

  const selectAll = () => onChange?.([...options]);
  const clearAll = () => onChange?.([]);

  const openMenu = () => {
    if (disabled) return;
    const rect = btnRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    setAnchorRect(rect);
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
        title="Filter status"
      >
        <span className="text-sm">{label}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {open && anchorRect && (
        <ActionMenuPortal open={open} anchorRect={anchorRect} onClose={close}>
          <div className="p-2">
            <div className="px-2 py-2 flex items-center justify-between">
              <div className="text-xs font-semibold text-gray-700">Filter Status</div>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100 text-gray-600"
                onClick={close}
                title="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-2 pb-2 flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-50"
                onClick={selectAll}
              >
                Select all
              </button>
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-50"
                onClick={clearAll}
              >
                Clear
              </button>
            </div>

            <div className="h-px bg-gray-100 my-1" />

            <div className="max-h-[260px] overflow-auto py-1">
              {options.map((st) => {
                const checked = normalized.includes(st);
                return (
                  <button
                    key={st}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => toggleOne(st)}
                  >
                    <span
                      className={[
                        "w-4 h-4 rounded border flex items-center justify-center",
                        checked ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300",
                      ].join(" ")}
                    >
                      {checked && <span className="block w-2 h-2 bg-white rounded-sm" />}
                    </span>
                    <span className="text-gray-800">{statusConfig[st]?.label || st}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </ActionMenuPortal>
      )}
    </>
  );
}

/* =========================================================
 * Void modal (reason required)
 * ========================================================= */
function VoidReasonModal({ open, invoiceLabel, reason, setReason, submitting, onCancel, onSubmit }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => textareaRef.current?.focus?.(), 80);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const canSubmit = String(reason || "").trim().length > 0 && !submitting;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-base sm:text-lg font-bold text-gray-900">Void Invoice</div>
            <div className="text-xs text-gray-500 mt-1 truncate">
              Invoice: <b>{invoiceLabel || "-"}</b>
            </div>
            <div className="text-sm text-gray-600 mt-2">Masukkan alasan void (wajib).</div>
          </div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-900 shrink-0"
            onClick={submitting ? undefined : onCancel}
          >
            Tutup
          </button>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Alasan void</label>
          <textarea
            ref={textareaRef}
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Contoh: Pembatalan order / revisi dokumen / salah nominal..."
            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            disabled={submitting}
          />
          <div className="mt-1 text-xs text-gray-500">Alasan akan tersimpan di invoice (voidReason).</div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 sm:px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="px-3 sm:px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-sm"
          >
            {submitting ? "Memproses..." : "Void Invoice"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* =========================================================
 * Download Invoice modal (include signature)
 * ========================================================= */
function DownloadInvoiceModal({ open, invoiceLabel, includeSignature, setIncludeSignature, submitting, onCancel, onSubmit }) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-gray-900">Download PDF</div>
            <div className="text-sm text-gray-600 mt-1 truncate">
              Invoice: <b>{invoiceLabel || "-"}</b>
            </div>
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-900"
            onClick={submitting ? undefined : onCancel}
            title="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-4 text-sm text-gray-700">Pilih apakah tanda tangan ikut dimasukkan ke PDF.</div>

        <button
          type="button"
          disabled={submitting}
          onClick={() => setIncludeSignature((v) => !v)}
          className="mt-3 w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
        >
          <span
            className={[
              "w-5 h-5 rounded border flex items-center justify-center",
              includeSignature ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300",
            ].join(" ")}
          >
            {includeSignature && <span className="block w-2.5 h-2.5 bg-white rounded-sm" />}
          </span>
          <span className="text-sm text-gray-800">Include signature</span>
        </button>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm inline-flex items-center gap-2"
          >
            {submitting ? "Downloading..." : "Download"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* =========================================================
 * SENT modal (PDF required)
 * ========================================================= */
function SentPdfModal({ open, title, subtitle, invoiceLabel, file, setFile, submitting, onCancel, onSubmit }) {
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fileRef.current?.focus?.(), 80);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const canSubmit = !!file && !submitting;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-base sm:text-lg font-bold text-gray-900">{title || "Upload PDF"}</div>
            <div className="text-xs text-gray-500 mt-1 truncate">
              Invoice: <b>{invoiceLabel || "-"}</b>
            </div>
            <div className="text-sm text-gray-600 mt-2">{subtitle || "Upload PDF dulu."}</div>
          </div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-900 shrink-0"
            onClick={submitting ? undefined : onCancel}
          >
            Tutup
          </button>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">PDF (wajib)</label>

          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={submitting}
            className="block w-full text-sm text-gray-700
                       file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0
                       file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700
                       hover:file:bg-gray-200"
          />

          {file && (
            <div className="mt-2 text-xs text-gray-600 break-all">
              Dipilih: <b>{file.name}</b> ({Math.round((file.size || 0) / 1024)} KB)
            </div>
          )}
          <div className="mt-2 text-xs text-gray-500">Hanya PDF yang diterima.</div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 sm:px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="px-3 sm:px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {submitting ? "Mengupload..." : "Upload"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* =========================================================
 * PAID modal (payment date + payment PDF required)
 * ========================================================= */
function PaidPaymentModal({ open, invoiceLabel, paymentDate, setPaymentDate, file, setFile, submitting, onCancel, onSubmit }) {
  const dateRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => dateRef.current?.focus?.(), 80);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const hasDate = !!String(paymentDate || "").trim();
  const hasFile = !!file;
  const canSubmit = hasDate && hasFile && !submitting;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 text-green-700">
              <Wallet className="w-4 h-4" />
              <span className="text-base sm:text-lg font-bold text-gray-900">Mark as Paid</span>
            </div>
            <div className="text-xs text-gray-500 mt-1 truncate">
              Invoice: <b>{invoiceLabel || "-"}</b>
            </div>
            <div className="text-sm text-gray-600 mt-2">
              Wajib isi tanggal pembayaran dan upload dokumen bukti pembayaran (PDF).
            </div>
          </div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-900 shrink-0"
            onClick={submitting ? undefined : onCancel}
          >
            Tutup
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date <span className="text-rose-600">*</span>
            </label>
            <input
              ref={dateRef}
              type="date"
              value={paymentDate || ""}
              onChange={(e) => setPaymentDate(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
            {!hasDate && <div className="mt-1 text-xs text-gray-500">Tanggal pembayaran belum dipilih.</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Document (PDF) <span className="text-rose-600">*</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={submitting}
              className="block w-full text-sm text-gray-700
                         file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0
                         file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700
                         hover:file:bg-gray-200"
            />
            {file && (
              <div className="mt-2 text-xs text-gray-600 break-all">
                Dipilih: <b>{file.name}</b> ({Math.round((file.size || 0) / 1024)} KB)
              </div>
            )}
            {!hasFile && <div className="mt-1 text-xs text-gray-500">Dokumen pembayaran belum dipilih.</div>}
            <div className="mt-1 text-xs text-gray-500">Hanya PDF yang diterima.</div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 sm:px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="px-3 sm:px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {submitting ? "Memproses..." : "Mark Paid"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Map invoice detail -> initialData for InvoiceForm (duplication) */
function mapInvoiceToFormInitialData(inv) {
  return {
    customerId: inv.customerId || "",
    invoiceDate: safeDate(inv.invoiceDate),
    dueDate: safeDate(inv.dueDate),
    lines: Array.isArray(inv.items)
      ? inv.items.map((it, idx) => ({
          id: `line-${idx + 1}`,
          productName: it.name || it.productName || "",
          description: it.description || "",
          qty: it.qty ?? 1,
          unitPrice: it.price ?? 0,
          discount: 0,
          discountType: "PERCENT",
          taxRate: 0,
          lineTotal: Number(it.amount ?? (Number(it.qty) || 0) * (Number(it.price) || 0)) || 0,
        }))
      : Array.isArray(inv.lines)
      ? inv.lines
      : [],
    signatureDate: safeDate(inv.meta?.signatureDate || inv.signatureDate),
    includeSignature: !!inv.includeSignature,

    from: inv.from,
    billTo: inv.billTo,
    payment: inv.payment,
    totals: inv.totals,
    status: "draft",
    signatureName: inv.signatureName || "",
    signatureTitle: inv.signatureTitle || "",
  };
}

export default function InvoicePenjualan({ isActive }) {
  const [showForm, setShowForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [createInitialData, setCreateInitialData] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  const [mitra, setMitra] = useState("");

  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidTargetId, setVoidTargetId] = useState(null);
  const [voidTargetLabel, setVoidTargetLabel] = useState("");
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  const [showSentModal, setShowSentModal] = useState(false);
  const [sentMode, setSentMode] = useState("mark");
  const [sentTargetId, setSentTargetId] = useState(null);
  const [sentTargetLabel, setSentTargetLabel] = useState("");
  const [sentFile, setSentFile] = useState(null);
  const [sentSubmitting, setSentSubmitting] = useState(false);

  const [showPaidModal, setShowPaidModal] = useState(false);
  const [paidTargetId, setPaidTargetId] = useState(null);
  const [paidTargetLabel, setPaidTargetLabel] = useState("");
  const [paidPaymentDate, setPaidPaymentDate] = useState("");
  const [paidPaymentFile, setPaidPaymentFile] = useState(null);
  const [paidSubmitting, setPaidSubmitting] = useState(false);

  // ✅ Download Invoice modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadTargetId, setDownloadTargetId] = useState(null);
  const [downloadTargetLabel, setDownloadTargetLabel] = useState("");
  const [downloadIncludeSignature, setDownloadIncludeSignature] = useState(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  
  const getFirstBlockingPrevInvoice = (inv, allInvoices) => {
  const salesId = getSalesId(inv);
  const currentIdx = Number(inv?.invoice_index || 0);
  if (!salesId || !(currentIdx > 0)) return null;

  const sameSale = (allInvoices || [])
    .filter((x) => getSalesId(x) === salesId)
    .sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0));

  return sameSale.find((x) => {
    const idx = Number(x?.invoice_index || 0);
    return idx > 0 && idx < currentIdx && !isPaidStatus(x?.status);
  });
};
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(
      Number(amount) || 0
    );

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });

  const closeMenu = useCallback(() => {
    setOpenMenuId(null);
    setMenuAnchorRect(null);
  }, []);

  const abortRef = useRef(null);
  const lastFetchKeyRef = useRef("");

  const buildFetchKey = useCallback(() => {
    return JSON.stringify({
      q: qDebounced || "",
    });
  }, [qDebounced]);

  const loadInvoices = useCallback(
    async ({ force = false } = {}) => {
      const key = buildFetchKey();

      if (!force) {
        const cached = loadInvoiceCache();
        if (cached?.key === key && Array.isArray(cached?.data)) {
          const age = Date.now() - Number(cached.ts || 0);
          if (age < CACHE_TTL_MS) {
            setInvoices(cached.data);
            setLoading(false);
            if (age < 5_000) return;
          } else {
            setInvoices(cached.data);
            setLoading(false);
          }
        }
      }

      if (!force && lastFetchKeyRef.current === key && invoices.length > 0) return;
      lastFetchKeyRef.current = key;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading((prev) => (invoices.length ? prev : true));

      try {
        const data = await apiFetch("/api/invoice-tracker/invoices", { method: "GET", signal: controller.signal });
        const list = Array.isArray(data) ? data : data?.items || [];
        setInvoices(list);
        saveInvoiceCache({ key, ts: Date.now(), data: list });
      } catch (err) {
        if (err?.name === "AbortError") return;
        showErr(err, "Gagal memuat daftar invoice");
      } finally {
        setLoading(false);
      }
    },
    [buildFetchKey, invoices.length]
  );

  useEffect(() => {
    loadInvoices({ force: false });
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadInvoices({ force: false });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [loadInvoices]);

  useEffect(() => {
    if (!isActive) return;
    loadInvoices({ force: true });
  }, [isActive, loadInvoices]);

  useEffect(() => {
    setPage(1);
  }, [q, pageSize, statusFilter, mitra]);



const getSalesId = (inv) => {
  // 1) Preferred: business Sales ID stored on invoice
  const ref = inv?.billTo?.reference;
  if (typeof ref === "string" && ref.trim()) return ref.trim();

  // 2) If invoice has populated sale object
  const populated = inv?.sales?.sales_id || inv?.sales?.salesId;
  if (typeof populated === "string" && populated.trim()) return populated.trim();

  // 3) Fallback: raw sales_id field (often ObjectId string)
  const v = inv?.sales_id || inv?.salesId || inv?.salesID;
  if (!v) return "";

  if (typeof v === "string") return v.trim();

  if (typeof v === "object") {
    const id = v?._id || v?.id || v?.sales_id || v?.salesId;
    return String(id || "").trim();
  }

  return String(v || "").trim();
};

const normalizeStatus = (st) => String(st || "").toLowerCase();

const isPaidStatus = (st) => {
  const s = normalizeStatus(st);
  return s === "paid";
};
const isSentOrBeyond = (st) => {
  const s = normalizeStatus(st);
  return s === "sent" || isPaidStatus(s);
};

/**
 * Only allow Mark Paid if:
 * - current status is sent/overdue (your current rule), AND
 * - if invoice has Sales ID: all earlier invoice_index (same Sales ID) are already paid.
 */
const canMarkPaidInOrder = (inv, allInvoices) => {
  const st = normalizeStatus(inv?.status);
  const baseAllowed = st === "sent" || st === "overdue";
  if (!baseAllowed) return false;

  const salesId = getSalesId(inv);
  if (!salesId) return true; // no sales → keep old behavior

  const currentIdx = Number(inv?.invoice_index || 0);
  if (!Number.isFinite(currentIdx) || currentIdx <= 0) return true; 
  // if invoice_index missing, don't block (or you can decide to block)

  const sameSale = (allInvoices || [])
    .filter((x) => getSalesId(x) === salesId)
    // include everything relevant for checking previous paid
    .sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0));

  // require all invoices with smaller invoice_index to be paid
  const prevUnpaid = sameSale.find((x) => {
    const idx = Number(x?.invoice_index || 0);
    if (!(idx > 0 && idx < currentIdx)) return false;
    return !isPaidStatus(x?.status);
  });

  return !prevUnpaid;
};

/**
 * Only allow Mark Sent if:
 * - base rule: status is "issued"
 * - if invoice has Sales ID: all earlier invoice_index (same Sales ID) are already sent (or beyond)
 */
const canMarkSentInOrder = (inv, allInvoices) => {
  const st = normalizeStatus(inv?.status);
  const baseAllowed = st === "issued"; // your current rule
  if (!baseAllowed) return false;

  const salesId = getSalesId(inv);
  if (!salesId) return true; // no sales → keep old behavior

  const currentIdx = Number(inv?.invoice_index || 0);
  if (!Number.isFinite(currentIdx) || currentIdx <= 0) return true;

  const sameSale = (allInvoices || [])
    .filter((x) => getSalesId(x) === salesId)
    .sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0));

  const prevNotSent = sameSale.find((x) => {
    const idx = Number(x?.invoice_index || 0);
    if (!(idx > 0 && idx < currentIdx)) return false;
    return !isSentOrBeyond(x?.status);
  });

  return !prevNotSent;
};

  const hasSalesId = (inv) => !!getSalesId(inv);

  const getCustomerName = (inv) => String(inv?.billTo?.companyName || inv?.customerName || "").trim();

  const getInvoiceNo = (inv) => String(inv?.invoiceNo || inv?.invoiceNumber || "").trim();

  const getIssuedId = (inv) => String(inv?.issued_id || inv?.issuedId || "").trim();

  const getDisplayInvoiceNo = (inv) => {
    const st = String(inv?.status || "draft").toLowerCase();
    const invoiceNo = inv?.invoiceNo || inv?.invoiceNumber || "-";
    const issued = inv?.issued_id || inv?.issuedId || "";
    if (st === "draft") return invoiceNo;
    return issued ? issued : invoiceNo;
  };

  const mitraOptions = useMemo(() => {
    const set = new Set();
    for (const inv of invoices || []) {
      const st = String(inv?.status || "draft").toLowerCase();
      if (!VISIBLE_STATUSES.includes(st)) continue;
      const name = getCustomerName(inv);
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const query = q.trim().toLowerCase();
    const selected = new Set((statusFilter || []).map((s) => String(s).toLowerCase()));
    const mitraLc = String(mitra || "").trim().toLowerCase();

    return (invoices || [])
      .filter((inv) => {
        const st = String(inv?.status || "draft").toLowerCase();
        return VISIBLE_STATUSES.includes(st);
      })
      .filter((inv) => {
        const st = String(inv?.status || "draft").toLowerCase();
        if (selected.size === 0) return false;
        return selected.has(st);
      })
      .filter((inv) => {
        if (!mitraLc) return true;
        return getCustomerName(inv).toLowerCase() === mitraLc;
      })
      .filter((inv) => {
        if (!query) return true;
        const invNo = getInvoiceNo(inv).toLowerCase();
        const issued = getIssuedId(inv).toLowerCase();
        const customerName = getCustomerName(inv).toLowerCase();
        const salesId = getSalesId(inv).toLowerCase();
        const statusEff = String(inv.status || "draft").toLowerCase();

        return (
          invNo.includes(query) ||
          issued.includes(query) ||
          customerName.includes(query) ||
          salesId.includes(query) ||
          statusEff.includes(query)
        );
      })
          // ✅ sort: earliest created first
      .sort((a, b) => {
        const ta = new Date(a?.createdAt || a?.created_at || a?.invoiceDate || 0).getTime() || 0;
        const tb = new Date(b?.createdAt || b?.created_at || b?.invoiceDate || 0).getTime() || 0;

        if (ta !== tb) return tb - ta; // newest first

        // tie-breakers (optional but nice)
        const sa = getSalesId(a);
        const sb = getSalesId(b);
        if (sa !== sb) return sa.localeCompare(sb, "id");

        const ia = Number(a?.invoice_index || 0);
        const ib = Number(b?.invoice_index || 0);
        return ib - ia;
      });

  }, [invoices, q, statusFilter, mitra]);

  const totalItems = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndexExcl = Math.min(startIndex + pageSize, totalItems);
  const pagedInvoices = useMemo(
    () => filteredInvoices.slice(startIndex, endIndexExcl),
    [filteredInvoices, startIndex, endIndexExcl]
  );


  const canEdit = (inv) => {
  const st = String(inv?.status || "").toLowerCase();
  // allow edit for all visible statuses (or exclude paid if you want)
  return VISIBLE_STATUSES.includes(st);
  // or: return VISIBLE_STATUSES.includes(st) && st !== "paid";
};

  const canMarkIssued = (inv) => String(inv?.status || "draft").toLowerCase() === "draft";
  const canMarkSent = (inv) => canMarkSentInOrder(inv, invoices);

  const canUpdateSentPdf = (inv) => String(inv?.status || "draft").toLowerCase() === "sent";
 const canMarkPaid = (inv) => canMarkPaidInOrder(inv, invoices);
const getFirstBlockingPrevInvoiceForSent = (inv, allInvoices) => {
  const salesId = getSalesId(inv);
  const currentIdx = Number(inv?.invoice_index || 0);
  if (!salesId || !(currentIdx > 0)) return null;

  const sameSale = (allInvoices || [])
    .filter((x) => getSalesId(x) === salesId)
    .sort((a, b) => Number(a?.invoice_index || 0) - Number(b?.invoice_index || 0));

  return sameSale.find((x) => {
    const idx = Number(x?.invoice_index || 0);
    return idx > 0 && idx < currentIdx && !isSentOrBeyond(x?.status);
  });
};


  // ✅ If created from Sales (has sales_id): remove Delete & Void actions
  const canDelete = (inv) => String(inv?.status || "draft").toLowerCase() === "draft" && !hasSalesId(inv);
  const canVoid = (inv) => {
    const st = String(inv?.status || "draft").toLowerCase();
    return (st === "issued" || st === "sent") && !hasSalesId(inv);
  };

  const hasSentDoc = (inv) => !!inv?.sent_document?.storage_path;
  const hasPaymentDoc = (inv) => !!inv?.payment_document?.storage_path;

  const handleCreateInvoice = async (payloadFromForm) => {
    try {
      const { _id, id, invoiceNo, invoiceNumber, createdAt, updatedAt, ...cleanPayload } = payloadFromForm || {};
      const created = await apiFetch("/api/invoice-tracker/invoices", {
        method: "POST",
        body: JSON.stringify(cleanPayload),
      });

      setInvoices((prev) => {
        const next = [created, ...(prev || [])];
        saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
        return next;
      });

      setShowForm(false);
      setCreateInitialData(null);

      setSelectedInvoice(created);
      setShowViewModal(true);
      showOk("Invoice berhasil dibuat.");
    } catch (err) {
      showErr(err, "Gagal membuat invoice");
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const detail = await apiFetch(`/api/invoice-tracker/invoices/${invoiceId}`, { method: "GET" });
      const st = String(detail?.status || "draft").toLowerCase();
      if (!VISIBLE_STATUSES.includes(st)) {
        showInfo("Invoice ini tidak ditampilkan di halaman ini.");
        return;
      }
      setSelectedInvoice(detail);
      setShowViewModal(true);
    } catch (err) {
      showErr(err, "Gagal memuat detail invoice");
    }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const detail = await apiFetch(`/api/invoice-tracker/invoices/${invoiceId}`, { method: "GET" });
      if (!canEdit(detail)) {
        showInfo("Invoice hanya bisa diedit saat status masih Draft.");
        return;
      }
      setEditingInvoice(detail);
      setShowViewModal(false);
    } catch (err) {
      showErr(err, "Gagal memuat invoice untuk edit");
    }
  };

  const handleEditFromView = () => {
    if (!selectedInvoice) return;
    if (!canEdit(selectedInvoice)) return showInfo("Invoice hanya bisa diedit saat status masih Draft.");
    setEditingInvoice(selectedInvoice);
    setShowViewModal(false);
  };

  const handleUpdateInvoice = async (payloadFromForm) => {
    try {
      const id = editingInvoice?._id || editingInvoice?.id;
      if (!id) throw new Error("Invoice id not found");

      const updated = await apiFetch(`/api/invoice-tracker/invoices/${id}`, {
        method: "PUT",
        body: JSON.stringify(payloadFromForm),
      });

      setInvoices((prev) => {
        const next = (prev || []).map((x) => (x._id === id || x.id === id ? updated : x));
        saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
        return next;
      });

      setEditingInvoice(null);
      setSelectedInvoice(updated);
      setShowViewModal(true);
      showOk("Invoice berhasil diupdate.");
    } catch (err) {
      showErr(err, "Gagal update invoice");
    }
  };

  const handleDuplicateInvoice = async (invoiceId) => {
    try {
      const detail = await apiFetch(`/api/invoice-tracker/invoices/${invoiceId}`, { method: "GET" });
      const st = String(detail?.status || "draft").toLowerCase();
      if (!VISIBLE_STATUSES.includes(st)) return showInfo("Invoice ini tidak bisa diduplicate dari halaman ini.");

      setCreateInitialData(mapInvoiceToFormInitialData(detail));
      setEditingInvoice(null);
      setShowViewModal(false);
      setShowForm(true);
    } catch (err) {
      showErr(err, "Gagal duplicate invoice");
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId, filenameBase = "invoice", options = {}) => {
    const includeSignature = !!options.includeSignature;
    const blob = await apiFetchBlob(`/api/invoice-tracker/invoices/${invoiceId}/pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ includeSignature }),
    });
    downloadBlob(blob, `${filenameBase}.pdf`);
  };

  const handleDownloadSentPdf = async (invoiceId, filenameBase = "invoice-sent") => {
    const blob = await apiFetchBlob(`/api/invoice-tracker/invoices/${invoiceId}/sent/download`, { method: "GET" });
    downloadBlob(blob, `${filenameBase}.pdf`);
  };

  // ✅ Download Bukti Bayar (payment_document)
  const handleDownloadBuktiBayar = async (invoiceId, filenameBase = "bukti-bayar") => {
    const blob = await apiFetchBlob(`/api/invoice-tracker/invoices/${invoiceId}/payment/download`, { method: "GET" });
    downloadBlob(blob, `${filenameBase}.pdf`);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await apiFetch(`/api/invoice-tracker/invoices/${invoiceId}`, { method: "DELETE" });
      setInvoices((prev) => {
        const next = (prev || []).filter((x) => (x._id || x.id) !== invoiceId);
        saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
        return next;
      });

      if ((selectedInvoice?._id || selectedInvoice?.id) === invoiceId) {
        setShowViewModal(false);
        setSelectedInvoice(null);
      }
      showOk("Invoice draft berhasil dihapus.");
    } catch (err) {
      showErr(err, "Gagal menghapus invoice");
    }
  };

  const patchStatusSingle = useCallback(
    async ({ id, status, voidReason }) => {
      const payload = {
        id,
        status,
        ...(status === "void" ? { voidReason: String(voidReason || "").trim() } : {}),
      };

      const res = await apiFetch("/api/invoice-tracker/invoices/status", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      const failed = Array.isArray(res?.failed) ? res.failed : [];
      if (failed.length) throw new Error(failed[0]?.message || "Status update failed");

      const updatedItems = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      if (updatedItems.length) {
        setInvoices((prev) => {
          const map = new Map((prev || []).map((x) => [String(x._id || x.id), x]));
          for (const u of updatedItems) {
  const key = String(u?._id || u?.id);
  const prevItem = map.get(key) || {};
  map.set(key, { ...prevItem, ...u }); 
}

          const next = Array.from(map.values());
          saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
          return next;
        });

        if (selectedInvoice) {
          const sid = String(selectedInvoice._id || selectedInvoice.id);
          const hit = updatedItems.find((x) => String(x._id || x.id) === sid);
          if (hit) setSelectedInvoice(hit);
        }
      }
      return updatedItems;
    },
    [selectedInvoice, buildFetchKey]
  );

  const markIssued = useCallback(
    async (inv) => {
      const id = inv?._id || inv?.id;
      if (!id) return;
      if (!canMarkIssued(inv)) return toast.info("Hanya Draft yang bisa ke Issued.");

      try {
        await patchStatusSingle({ id, status: "issued" });
        showOk("Invoice berhasil diubah ke Issued.");
      } catch (err) {
        showErr(err, "Gagal ubah ke Issued");
      }
    },
    [patchStatusSingle]
  );

  const submitSentOrUpdate = useCallback(async () => {
    if (!sentTargetId) return;
    if (!sentFile) return toast.warning("PDF wajib dipilih.");

    const isPdf =
      String(sentFile.type || "").toLowerCase() === "application/pdf" ||
      String(sentFile.name || "").toLowerCase().endsWith(".pdf");

    if (!isPdf) return toast.warning("File harus PDF.");

    setSentSubmitting(true);
    try {
      const signed = await apiFetch(`/api/invoice-tracker/invoices/${sentTargetId}/sent/signed-url`, {
        method: "POST",
        body: JSON.stringify({ fileName: sentFile.name || "invoice-sent.pdf", contentType: "application/pdf" }),
      });

      const signedUrl = signed?.signedUrl;
      const objectPath = signed?.objectPath;
      const bucket = signed?.bucket;

      if (!signedUrl || !objectPath || !bucket) {
        throw new Error("Signed URL response invalid (missing signedUrl/objectPath/bucket)");
      }

      await uploadToSignedUrl({ signedUrl, file: sentFile, contentType: "application/pdf" });

      const payload = {
        sent_document: {
          provider: "gcs",
          bucket,
          storage_path: objectPath,
          content_type: "application/pdf",
          display_name: sentFile.name || "invoice.pdf",
        },
      };

      const updated = await apiFetch(`/api/invoice-tracker/invoices/${sentTargetId}/sent/commit`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const uid = String(updated?._id || updated?.id || sentTargetId);
      setInvoices((prev) => {
        const map = new Map((prev || []).map((x) => [String(x._id || x.id), x]));
        map.set(uid, updated);
        const next = Array.from(map.values());
        saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
        return next;
      });
      if (selectedInvoice && String(selectedInvoice._id || selectedInvoice.id) === uid) setSelectedInvoice(updated);

      showOk(sentMode === "update" ? "Sent PDF berhasil diupdate." : "Invoice berhasil diubah ke Sent (PDF tersimpan).");

      setShowSentModal(false);
      setSentMode("mark");
      setSentTargetId(null);
      setSentTargetLabel("");
      setSentFile(null);
      setSentSubmitting(false);
    } catch (err) {
      showErr(err, sentMode === "update" ? "Gagal update sent PDF" : "Gagal mark as sent");
      setSentSubmitting(false);
    }
  }, [sentTargetId, sentFile, selectedInvoice, sentMode, buildFetchKey]);

  const submitPaid = useCallback(async () => {
    if (!paidTargetId) return;
    const current = (invoices || []).find((x) => String(x._id || x.id) === String(paidTargetId));
    const prevStatus = String(current?.status || "").toLowerCase();
    const nextPaidStatus = "paid";


    const paymentDate = String(paidPaymentDate || "").trim();
    const file = paidPaymentFile;

    if (!paymentDate) return toast.warning("Payment Date wajib diisi.");
    if (!file) return toast.warning("Payment Document (PDF) wajib diupload.");

    const isPdf = String(file.type || "").toLowerCase() === "application/pdf" || String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) return toast.warning("Payment Document harus PDF.");

    setPaidSubmitting(true);
    try {
      const signed = await apiFetch(`/api/invoice-tracker/invoices/${paidTargetId}/payment/signed-url`, {
        method: "POST",
        body: JSON.stringify({ fileName: file.name || "payment-proof.pdf", contentType: "application/pdf" }),
      });

      const signedUrl = signed?.signedUrl;
      const objectPath = signed?.objectPath;
      const bucket = signed?.bucket;

      if (!signedUrl || !objectPath || !bucket) {
        throw new Error("Signed URL response invalid (missing signedUrl/objectPath/bucket)");
      }

      await uploadToSignedUrl({ signedUrl, file, contentType: "application/pdf" });

      const payload = {
        paymentDate,
        status: nextPaidStatus,
        payment_document: {
          provider: "gcs",
          bucket,
          storage_path: objectPath,
          content_type: "application/pdf",
          display_name: file.name || "payment-proof.pdf",
        },
      };

      const updated = await apiFetch(`/api/invoice-tracker/invoices/${paidTargetId}/payment/commit`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const uid = String(updated?._id || updated?.id || paidTargetId);
      setInvoices((prev) => {
        const map = new Map((prev || []).map((x) => [String(x._id || x.id), x]));
        map.set(uid, updated);
        const next = Array.from(map.values());
        saveInvoiceCache({ key: buildFetchKey(), ts: Date.now(), data: next });
        return next;
      });
      if (selectedInvoice && String(selectedInvoice._id || selectedInvoice.id) === uid) setSelectedInvoice(updated);

      showOk("Invoice berhasil diubah ke Paid (payment date + payment document tersimpan).");

      setShowPaidModal(false);
      setPaidTargetId(null);
      setPaidTargetLabel("");
      setPaidPaymentDate("");
      setPaidPaymentFile(null);
      setPaidSubmitting(false);
    } catch (err) {
      showErr(err, "Gagal mark as paid");
      setPaidSubmitting(false);
    }
  }, [paidTargetId, paidPaymentDate, paidPaymentFile, selectedInvoice, buildFetchKey, invoices]);

  const submitVoid = useCallback(async () => {
    const id = voidTargetId;
    const reason = String(voidReason || "").trim();
    if (!id) return;
    if (!reason) return toast.warning("Alasan void wajib diisi.");

    setVoidSubmitting(true);
    try {
      await patchStatusSingle({ id, status: "void", voidReason: reason });
      showOk("Invoice berhasil di-void.");

      setShowVoidModal(false);
      setVoidTargetId(null);
      setVoidTargetLabel("");
      setVoidReason("");
      setVoidSubmitting(false);

      await loadInvoices({ force: true });
    } catch (err) {
      showErr(err, "Gagal void invoice");
      setVoidSubmitting(false);
    }
  }, [voidTargetId, voidReason, patchStatusSingle, loadInvoices]);

  const openDownloadInvoiceModal = useCallback(
    (id, displayNo) => {
      setDownloadTargetId(id);
      setDownloadTargetLabel(displayNo || "-");
      setDownloadIncludeSignature(false);
      setDownloadSubmitting(false);
      setShowDownloadModal(true);
    },
    []
  );

  const submitDownloadInvoice = useCallback(async () => {
    if (!downloadTargetId) return;
    setDownloadSubmitting(true);
    try {
      await handleDownloadInvoicePdf(downloadTargetId, downloadTargetLabel || "invoice", {
        includeSignature: downloadIncludeSignature,
      });
      showOk("PDF berhasil didownload.");
      setShowDownloadModal(false);
      setDownloadTargetId(null);
      setDownloadTargetLabel("");
      setDownloadIncludeSignature(false);
    } catch (err) {
      showErr(err, "Gagal download PDF");
    } finally {
      setDownloadSubmitting(false);
    }
  }, [downloadTargetId, downloadTargetLabel, downloadIncludeSignature]);

  return (
    <>
      {showForm && (
        <InvoiceForm
          onClose={() => {
            setShowForm(false);
            setCreateInitialData(null);
          }}
          onSubmit={handleCreateInvoice}
          initialData={createInitialData || {}}
        />
      )}

      {showViewModal && selectedInvoice && (
        <InvoiceViewModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInvoice(null);
          }}
          onEdit={handleEditFromView}
          onDownloadPdf={({ includeSignature }) => {
            const id = selectedInvoice?._id || selectedInvoice?.id;
            const filename = getDisplayInvoiceNo(selectedInvoice) || "invoice";
            if (!id) return toast.warning("Invoice id tidak ditemukan");
            // keep existing behavior for view modal
            return handleDownloadInvoicePdf(id, filename, { includeSignature: !!includeSignature })
              .then(() => showOk("PDF berhasil didownload."))
              .catch((err) => showErr(err, "Gagal download PDF"));
          }}
        />
      )}

      {editingInvoice && (
        <InvoiceForm
          onClose={() => setEditingInvoice(null)}
          onSubmit={handleUpdateInvoice}
          initialData={{
            _id: editingInvoice._id || editingInvoice.id,
            invoiceNo: editingInvoice.invoiceNo || editingInvoice.invoiceNumber || "",
            customerId: editingInvoice.customerId || "",
            invoiceDate: safeDate(editingInvoice.invoiceDate),
            dueDate: safeDate(editingInvoice.dueDate),
            lines: Array.isArray(editingInvoice.items)
              ? editingInvoice.items.map((it, idx) => ({
                  id: `line-${idx + 1}`,
                  productName: it.name || it.productName || "",
                  description: it.description || "",
                  qty: it.qty ?? 1,
                  unitPrice: it.price ?? 0,
                  discount: 0,
                  discountType: "PERCENT",
                  taxRate: 0,
                  lineTotal: Number(it.amount ?? (Number(it.qty) || 0) * (Number(it.price) || 0)) || 0,
                }))
              : editingInvoice.lines || [],
            signatureDate: safeDate(editingInvoice.meta?.signatureDate || editingInvoice.signatureDate),
            from: editingInvoice.from,
            billTo: editingInvoice.billTo,
            payment: editingInvoice.payment,
            totals: editingInvoice.totals,
            includeSignature: !!editingInvoice.includeSignature,
            signatureName: editingInvoice.signatureName || "",
            signatureTitle: editingInvoice.signatureTitle || "",
            status: editingInvoice.status || "draft",
            sales_id: editingInvoice.sales_id || editingInvoice.salesId,
            sales: editingInvoice.sales,

          }}
        />
      )}

      <VoidReasonModal
        open={showVoidModal}
        invoiceLabel={voidTargetLabel}
        reason={voidReason}
        setReason={setVoidReason}
        submitting={voidSubmitting}
        onCancel={() => {
          setShowVoidModal(false);
          setVoidTargetId(null);
          setVoidTargetLabel("");
          setVoidReason("");
          setVoidSubmitting(false);
        }}
        onSubmit={submitVoid}
      />

      <DownloadInvoiceModal
        open={showDownloadModal}
        invoiceLabel={downloadTargetLabel}
        includeSignature={downloadIncludeSignature}
        setIncludeSignature={setDownloadIncludeSignature}
        submitting={downloadSubmitting}
        onCancel={() => {
          setShowDownloadModal(false);
          setDownloadTargetId(null);
          setDownloadTargetLabel("");
          setDownloadIncludeSignature(false);
          setDownloadSubmitting(false);
        }}
        onSubmit={submitDownloadInvoice}
      />

      <SentPdfModal
        open={showSentModal}
        title={sentMode === "update" ? "Update Sent PDF" : "Mark as Sent"}
        subtitle={
          sentMode === "update"
            ? "Upload PDF baru untuk mengganti dokumen sent."
            : "Upload PDF bukti pengiriman dulu. Setelah sukses upload, status berubah ke sent."
        }
        invoiceLabel={sentTargetLabel}
        file={sentFile}
        setFile={setSentFile}
        submitting={sentSubmitting}
        onCancel={() => {
          setShowSentModal(false);
          setSentMode("mark");
          setSentTargetId(null);
          setSentTargetLabel("");
          setSentFile(null);
          setSentSubmitting(false);
        }}
        onSubmit={submitSentOrUpdate}
      />

      <PaidPaymentModal
        open={showPaidModal}
        invoiceLabel={paidTargetLabel}
        paymentDate={paidPaymentDate}
        setPaymentDate={setPaidPaymentDate}
        file={paidPaymentFile}
        setFile={setPaidPaymentFile}
        submitting={paidSubmitting}
        onCancel={() => {
          setShowPaidModal(false);
          setPaidTargetId(null);
          setPaidTargetLabel("");
          setPaidPaymentDate("");
          setPaidPaymentFile(null);
          setPaidSubmitting(false);
        }}
        onSubmit={submitPaid}
      />

      <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Invoice Penjualan</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Kelola data invoice penjualan perusahaan</p>
          </div>

          <button
            type="button"
            onClick={() => {
              setCreateInitialData(null);
              setEditingInvoice(null);
              setShowViewModal(false);
              setSelectedInvoice(null);
              closeMenu();
              setShowForm(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Buat Invoice Baru</span>
            <span className="sm:hidden">Buat Invoice</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          {/* Filters */}
          <div className="p-3 sm:p-6 border-b border-gray-200 space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Search */}
              <div className="flex-1 relative">

                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={q}
                 onChange={(e) => setQ(e.target.value)}

                  placeholder="Cari Invoice, Customer..."
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Mitra dropdown */}
              <select
                value={mitra}
                onChange={(e) => setMitra(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full sm:w-auto sm:min-w-[180px]"
                title="Filter Mitra"
                disabled={loading}
              >
                <option value="">Semua Mitra</option>
                {mitraOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>

              {/* Status multi-select */}
              <StatusMultiSelect
                value={statusFilter}
                onChange={(next) => setStatusFilter(next)}
                options={VISIBLE_STATUSES}
                disabled={loading}
              />
            </div>

            <div className="text-xs text-gray-500">
              {loading ? "Memuat data..." : q !== qDebounced ? "Mengetik..." : "Data otomatis diperbarui"} • Alur:{" "}
              <b className="hidden sm:inline">Draft → Issued → Sent → Paid</b>
              <b className="sm:hidden">D→I→S→P</b>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    No. Invoice
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    TOP
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider">Jumlah</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    Tanggal
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    Jatuh Tempo
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    Payment Date
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 tracking-wider whitespace-nowrap">
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {pagedInvoices.map((inv) => {
                  const id = inv._id || inv.id;
                  const displayNo = getDisplayInvoiceNo(inv);
                  const topIndex = inv?.invoice_index;
                  const topLabel = topIndex ? toOrdinal(topIndex) : "-";
                  const customerName = inv.billTo?.companyName || inv.customerName || "-";
                  const amount = inv.totals?.grandTotal ?? inv.amount ?? 0;
                  const date = inv.invoiceDate || inv.date;
                  const dueDate = inv.dueDate || inv.due_date || inv.meta?.dueDate;
                  const paymentDate = inv.paymentDate;
                  const statusEff = String(inv.status || "draft").toLowerCase();
                  const isPaid = statusEff === "paid";

                  return (
                    <tr key={id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-center align-middle">
                        <span className="font-medium text-gray-900 text-sm">{displayNo}</span>
                      </td>

                      <td className="px-4 py-4 text-center text-gray-700 align-middle">
  <div className="flex items-center justify-center min-h-[36px]">
    <span className="max-w-[240px] break-words line-clamp-2 text-sm leading-5">
      {customerName}
    </span>
  </div>
</td>

                      <td className="px-4 py-4 text-center align-middle">
                        <span className="font-medium text-gray-900 text-sm">{topLabel}</span>
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <span className="font-semibold text-gray-900 text-sm">{formatCurrency(amount)}</span>
                      </td>

                      <td className="px-4 py-4 text-center text-gray-700 whitespace-nowrap align-middle text-sm">
                        {date ? formatDate(date) : "-"}
                      </td>

                      <td className="px-4 py-4 text-center text-gray-700 whitespace-nowrap align-middle text-sm">
                        {dueDate ? formatDate(dueDate) : "-"}
                      </td>

                      <td className="px-4 py-4 text-center text-gray-700 whitespace-nowrap align-middle text-sm">
                        {paymentDate ? formatDate(paymentDate) : "Unpaid"}
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <span
                          className={[
                            "inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap",
                            statusConfig[statusEff]?.className || "bg-gray-100 text-gray-700",
                          ].join(" ")}
                        >
                          {statusConfig[statusEff]?.label || statusEff}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-center align-middle">
                        <div className="relative inline-flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeMenu();
                              handleViewInvoice(id);
                            }}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Lihat Detail"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setOpenMenuId((prev) => {
                                const next = prev === id ? null : id;
                                if (next) setMenuAnchorRect(rect);
                                else setMenuAnchorRect(null);
                                return next;
                              });
                            }}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                            title="Aksi lain"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          <ActionMenuPortal
                            open={openMenuId === id}
                            anchorRect={openMenuId === id ? menuAnchorRect : null}
                            onClose={closeMenu}
                          >
                            {canEdit(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-blue-700"
                                onClick={() => {
                                  closeMenu();
                                  handleEditInvoice(id);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                            )}

                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => {
                                closeMenu();
                                handleDuplicateInvoice(id);
                              }}
                            >
                              <Copy className="w-4 h-4 text-gray-700" />
                              Duplicate
                            </button>

                            {/* ✅ Rename "Download PDF" -> "Download Invoice" + open modal */}
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => {
                                closeMenu();
                                openDownloadInvoiceModal(id, displayNo);
                              }}
                            >
                              <Download className="w-4 h-4 text-gray-700" />
                              Download Invoice
                            </button>

                            {hasSentDoc(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => {
                                  closeMenu();
                                  handleDownloadSentPdf(id, `${displayNo}-sent`)
                                    .then(() => showOk("Sent PDF berhasil didownload."))
                                    .catch((err) => showErr(err, "Gagal download Sent PDF"));
                                }}
                              >
                                <Download className="w-4 h-4 text-sky-700" />
                                Download Sent PDF
                              </button>
                            )}

                            {/* ✅ When Lunas: show Download Bukti Bayar */}
                            {isPaid && hasPaymentDoc(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                onClick={() => {
                                  closeMenu();
                                  handleDownloadBuktiBayar(id, `${displayNo}-bukti-bayar`)
                                    .then(() => showOk("Bukti Bayar berhasil didownload."))
                                    .catch((err) => showErr(err, "Gagal download Bukti Bayar"));
                                }}
                              >
                                <Download className="w-4 h-4 text-emerald-700" />
                                Download Bukti Bayar
                              </button>
                            )}

                            <div className="h-px bg-gray-100" />

                            {canMarkIssued(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex items-center gap-2 text-indigo-700"
                                onClick={() => {
                                  closeMenu();
                                  markIssued(inv);
                                }}
                              >
                                <Send className="w-4 h-4" />
                                Mark Issued
                              </button>
                            )}

                           {canMarkSent(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2 text-sky-700"
                                onClick={() => {
                                  closeMenu();
                                  setSentMode("mark");
                                  setSentTargetId(id);
                                  setSentTargetLabel(displayNo);
                                  setSentFile(null);
                                  setSentSubmitting(false);
                                  setShowSentModal(true);
                                }}
                              >
                                <Send className="w-4 h-4" />
                                Mark Sent
                              </button>
                            )}


                            {canUpdateSentPdf(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2 text-sky-700"
                                onClick={() => {
                                  closeMenu();
                                  setSentMode("update");
                                  setSentTargetId(id);
                                  setSentTargetLabel(displayNo);
                                  setSentFile(null);
                                  setSentSubmitting(false);
                                  setShowSentModal(true);
                                }}
                              >
                                <Upload className="w-4 h-4" />
                                Update Sent PDF
                              </button>
                            )}

                            {canMarkPaid(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 flex items-center gap-2 text-green-700"
                            onClick={() => {
                              closeMenu();

                              const ok = canMarkPaidInOrder(inv, invoices);
                              if (!ok) {
                                const prev = getFirstBlockingPrevInvoice(inv, invoices);
                                const prevNo = prev ? getDisplayInvoiceNo(prev) : "invoice sebelumnya";
                                return toast.info(`Tidak bisa Mark Paid. Selesaikan pembayaran ${prevNo} dulu.`);
                              }

                              setPaidTargetId(id);
                              setPaidTargetLabel(displayNo);
                              setPaidPaymentDate("");
                              setPaidPaymentFile(null);
                              setPaidSubmitting(false);
                              setShowPaidModal(true);
                            }}

                              >
                                <CheckCircle2 className="w-4 h-4" />
                                Mark Paid
                              </button>
                            )}

                            <div className="h-px bg-gray-100" />

                            {canDelete(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                onClick={() => {
                                  closeMenu();
                                  handleDeleteInvoice(id);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            )}

                            {canVoid(inv) && (
                              <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                onClick={() => {
                                  closeMenu();
                                  setVoidTargetId(id);
                                  setVoidTargetLabel(displayNo);
                                  setVoidReason("");
                                  setVoidSubmitting(false);
                                  setShowVoidModal(true);
                                }}
                              >
                                <Ban className="w-4 h-4" />
                                Void
                              </button>
                            )}
                          </ActionMenuPortal>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!pagedInvoices.length && (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-gray-500">
                      {loading ? "Memuat data..." : "Tidak ada invoice yang cocok."}
                  
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-gray-200">
            {pagedInvoices.map((inv) => {
              const id = inv._id || inv.id;
              const displayNo = getDisplayInvoiceNo(inv);
              const topIndex = inv?.invoice_index;
              const topLabel = topIndex ? toOrdinal(topIndex) : "-";
              const customerName = inv.billTo?.companyName || inv.customerName || "-";
              const amount = inv.totals?.grandTotal ?? inv.amount ?? 0;
              const date = inv.invoiceDate || inv.date;
              const dueDate = inv.dueDate || inv.due_date || inv.meta?.dueDate;
              const paymentDate = inv.paymentDate;
              const statusEff = String(inv.status || "draft").toLowerCase();
              const isPaid = statusEff === "paid" || statusEff === "paid overdue" || statusEff === "paid_overdue";

              return (
                <div key={id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">{displayNo}</div>
                      <div className="text-xs text-gray-600 mt-1 break-words line-clamp-2">{customerName}</div>
                    </div>
                    <span
                      className={[
                        "inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap shrink-0",
                        statusConfig[statusEff]?.className || "bg-gray-100 text-gray-700",
                      ].join(" ")}
                    >
                      {statusConfig[statusEff]?.label || statusEff}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-gray-500">TOP</div>
                      <div className="font-medium text-gray-900 mt-0.5">{topLabel}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Jumlah</div>
                      <div className="font-semibold text-gray-900 mt-0.5">{formatCurrency(amount)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Tanggal</div>
                      <div className="text-gray-700 mt-0.5">{date ? formatDate(date) : "-"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Jatuh Tempo</div>
                      <div className="text-gray-700 mt-0.5">{dueDate ? formatDate(dueDate) : "-"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Payment Date</div>
                      <div className="text-gray-700 mt-0.5">{paymentDate ? formatDate(paymentDate) : "Unpaid"}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => handleViewInvoice(id)}
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-blue-600 hover:bg-blue-50 transition-colors inline-flex items-center justify-center gap-2 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Lihat
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setOpenMenuId((prev) => {
                          const next = prev === id ? null : id;
                          if (next) setMenuAnchorRect(rect);
                          else setMenuAnchorRect(null);
                          return next;
                        });
                      }}
                      className="px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center justify-center text-sm"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    <ActionMenuPortal
                      open={openMenuId === id}
                      anchorRect={openMenuId === id ? menuAnchorRect : null}
                      onClose={closeMenu}
                    >
                      {canEdit(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-blue-700"
                          onClick={() => {
                            closeMenu();
                            handleEditInvoice(id);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                      )}

                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => {
                          closeMenu();
                          handleDuplicateInvoice(id);
                        }}
                      >
                        <Copy className="w-4 h-4 text-gray-700" />
                        Duplicate
                      </button>

                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => {
                          closeMenu();
                          openDownloadInvoiceModal(id, displayNo);
                        }}
                      >
                        <Download className="w-4 h-4 text-gray-700" />
                        Download Invoice
                      </button>

                      {hasSentDoc(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          onClick={() => {
                            closeMenu();
                            handleDownloadSentPdf(id, `${displayNo}-sent`)
                              .then(() => showOk("Sent PDF berhasil didownload."))
                              .catch((err) => showErr(err, "Gagal download Sent PDF"));
                          }}
                        >
                          <Download className="w-4 h-4 text-sky-700" />
                          Download Sent PDF
                        </button>
                      )}

                      {isPaid && hasPaymentDoc(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          onClick={() => {
                            closeMenu();
                            handleDownloadBuktiBayar(id, `${displayNo}-bukti-bayar`)
                              .then(() => showOk("Bukti Bayar berhasil didownload."))
                              .catch((err) => showErr(err, "Gagal download Bukti Bayar"));
                          }}
                        >
                          <Download className="w-4 h-4 text-emerald-700" />
                          Download Bukti Bayar
                        </button>
                      )}

                      <div className="h-px bg-gray-100" />

                      {canMarkIssued(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 flex items-center gap-2 text-indigo-700"
                          onClick={() => {
                            closeMenu();
                            markIssued(inv);
                          }}
                        >
                          <Send className="w-4 h-4" />
                          Mark Issued
                        </button>
                      )}

                      {canMarkSent(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2 text-sky-700"
                         onClick={() => {
                          closeMenu();

                          const ok = canMarkSentInOrder(inv, invoices);
                          if (!ok) {
                            const prev = getFirstBlockingPrevInvoiceForSent(inv, invoices);
                            const prevNo = prev ? getDisplayInvoiceNo(prev) : "invoice sebelumnya";
                            return toast.info(`Tidak bisa Mark Sent. Selesaikan pengiriman ${prevNo} dulu.`);
                          }

                          setSentMode("mark");
                          setSentTargetId(id);
                          setSentTargetLabel(displayNo);
                          setSentFile(null);
                          setSentSubmitting(false);
                          setShowSentModal(true);
                        }}

                        >
                          <Send className="w-4 h-4" />
                          Mark Sent
                        </button>
                      )}

                      {canUpdateSentPdf(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center gap-2 text-sky-700"
                          onClick={() => {
                            closeMenu();
                            setSentMode("update");
                            setSentTargetId(id);
                            setSentTargetLabel(displayNo);
                            setSentFile(null);
                            setSentSubmitting(false);
                            setShowSentModal(true);
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          Update Sent PDF
                        </button>
                      )}

                      {canMarkPaid(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 flex items-center gap-2 text-green-700"
                          onClick={() => {
                            closeMenu();
                            setPaidTargetId(id);
                            setPaidTargetLabel(displayNo);
                            setPaidPaymentDate("");
                            setPaidPaymentFile(null);
                            setPaidSubmitting(false);
                            setShowPaidModal(true);
                          }}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Mark Paid
                        </button>
                      )}

                      <div className="h-px bg-gray-100" />

                      {canDelete(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                          onClick={() => {
                            closeMenu();
                            handleDeleteInvoice(id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}

                      {canVoid(inv) && (
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                          onClick={() => {
                            closeMenu();
                            setVoidTargetId(id);
                            setVoidTargetLabel(displayNo);
                            setVoidReason("");
                            setVoidSubmitting(false);
                            setShowVoidModal(true);
                          }}
                        >
                          <Ban className="w-4 h-4" />
                          Void
                        </button>
                      )}
                    </ActionMenuPortal>
                  </div>
                </div>
              );
            })}

            {!pagedInvoices.length && (
              <div className="p-6 text-center text-gray-500">
                <div className="text-sm">{loading ? "Memuat data..." : "Tidak ada invoice yang cocok."}</div>
                <button
                  type="button"
                  onClick={() => {
                    setCreateInitialData(null);
                    closeMenu();
                    setShowForm(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Buat Invoice Baru
                </button>
              </div>
            )}
          </div>

          {/* Footer / Pagination */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="text-xs sm:text-sm text-gray-600">Rows:</div>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 sm:px-3 py-1 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <div className="text-xs sm:text-sm text-gray-600 ml-2">
                {totalItems === 0 ? (
                  <>
                    <span className="font-semibold">0</span> invoice
                  </>
                ) : (
                  <>
                    <span className="font-semibold">
                      {startIndex + 1}-{endIndexExcl}
                    </span>
                    <span className="hidden sm:inline"> dari </span>
                    <span className="sm:hidden">/</span>
                    <span className="font-semibold">{totalItems}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-2 sm:px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>

              <div className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                <b>{currentPage}</b> / <b>{totalPages}</b>
              </div>

              <button
                type="button"
                className="px-2 sm:px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
